import React from "react";
import { FaEdit, FaEyeSlash, FaSearch, FaTrash } from "react-icons/fa";

import {
  formatDate,
  getNewsDate,
  resolveMediaUrl,
  truncateText,
} from "../utils/actualitesUtils";

export default function ActualitesTable({
  rows,
  loading,
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Tableau des actualités
            </h2>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {rows.length.toLocaleString("fr-FR")} actualité
              {rows.length > 1 ? "s" : ""} affichée
              {rows.length > 1 ? "s" : ""}.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-[minmax(240px,1fr)_180px] xl:min-w-[560px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Rechercher un titre ou contenu..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">Toutes</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1080px] w-full text-sm">
            <thead className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Image
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Actualité
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Aperçu contenu
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Date
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-12 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucune actualité ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                rows.map((article) => (
                  <tr
                    key={article.id}
                    className={[
                      "transition hover:bg-slate-50 dark:hover:bg-slate-900",
                      !article.is_active
                        ? "bg-slate-50 text-slate-500 dark:bg-slate-900/60"
                        : "",
                    ].join(" ")}
                  >
                    <td className="px-4 py-3">
                      <img
                        src={resolveMediaUrl(article.image_url)}
                        alt={article.title}
                        className="h-16 w-24 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-white/10"
                        onError={(event) => {
                          event.currentTarget.src =
                            "/images/asso_placeholder.webp";
                        }}
                      />
                    </td>

                    <td className="px-4 py-3">
                      <div className="max-w-[260px]">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {article.title || "Sans titre"}
                        </p>

                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          ID {article.id}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      <div className="max-w-[360px] leading-6">
                        {truncateText(article.content, 160)}
                      </div>
                    </td>

                    <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">
                      {formatDate(getNewsDate(article))}
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge active={article.is_active} />
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(article)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(article.id)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
          <FaEyeSlash className="mr-2 inline text-slate-400" />
          Une actualité inactive reste enregistrée dans l’administration mais
          n’apparaît pas sur le site public.
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      ].join(" ")}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
}